import React from "react";
import { Typography } from "@aph/ui-lib-humanx";
import BackgroundComponent from "@/components/ValentinePopup/components/BackgroundComponent";
import styles from "@/components/ValentinePopup/popup.module.scss";
import IconLogo from "$/valentine/logo.svg";
import IconLeft from "$/icons/icon-left.svg";
import IconRight from "$/icons/icon-right.svg";
import ValentineShareModal from "@/components/ValentinePopup/ValentineShareModal";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import ValentineShareModalSelector from "@/components/ValentinePopup/redux/selectors/shareModalSelector";
import { setImageName, setIsOpenShareModal } from "@/components/ValentinePopup/redux/slice";
import ValentineCardImageSelector from "@/components/ValentinePopup/redux/selectors/valentineCardImage";
import { previewSlide, slides } from "@/components/ValentinePopup/helpers";
import { DeviceInfo } from "@/interface/deviceInfo";

interface CardsComponentProps {
  formData: {
    title: string;
    description: string;
  };
  setFormData: (data: any) => void;
  cardRef: any;
  deviceInfo: DeviceInfo;
}

const CardsComponent: React.FC<CardsComponentProps> = ({ formData, setFormData, cardRef, deviceInfo }) => {
  const [activeSlide, setActiveSlide] = React.useState<number>(0);
  const [isPreview, setIsPreview] = React.useState(false);
  const sliderRef = React.useRef(null);
  const [position, setPosition] = React.useState(0);
  const [startX, setStartX] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const cardName = useAppSelector(ValentineCardImageSelector);

  const dispatch = useAppDispatch();
  const isShareModalOpen = useAppSelector(ValentineShareModalSelector);

  const checkPreviewBtnActive = () => {
    return formData && Object.entries(formData) ? Object.values(formData).every((val) => val !== "") : false;
  };

  const handleOpenShareModal = (isOpen: boolean) => {
    dispatch(setIsOpenShareModal(isOpen));
  };

  const handleShareCard = () => {
    dispatch(setImageName(`card${activeSlide + 1}.png`));
    dispatch(setIsOpenShareModal(true));
  };

  const handleCustomCardSubmit = async () => {
    if (cardName?.length > 0) {
      dispatch(setIsOpenShareModal(true));
    }
  };

  const openNextSlide = () => {
    setActiveSlide((prev) => Math.min(prev + 1, slides?.length - 1));
  };

  const openPrevSlide = () => {
    setActiveSlide((prev) => Math.max(prev - 1, 0));
  };

  const cardBtn = {
    sendBtn: (
      <>
        <button className={styles.btn} onClick={handleShareCard}>
          Send this to my partner
        </button>
      </>
    ),
    previewBtn: (
      <button
        className={`${checkPreviewBtnActive() ? styles.active : ""} ${styles.btn} ${styles.previewBtn}`}
        onClick={() => {
          setIsPreview(true);
        }}
      >
        Preview the card
      </button>
    ),
    submitBtn: (
      <div className={styles.btnWrapper}>
        <button
          className={`${styles.btn} ${styles.backBtn}`}
          onClick={() => {
            setIsPreview(false);
          }}
        >
          Go back to edit
        </button>
        <button className={`${styles.btn} ${styles.sendBtn}`} onClick={handleCustomCardSubmit}>
          Send this one
        </button>
      </div>
    ),
  };

  const handleStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX || e.clientX : e.movementX;
    setStartX(clientX);
    setPosition(clientX);
    setIsDragging(true);
    sliderRef.current.style.cursor = "grabbing";
  };

  const handleMove = (e) => {
    if (!isDragging) return;

    if (e.touches) {
      const clientX = e.touches[0].clientX || e.clientX;
      const deltaX = clientX - startX;
      setPosition((prev) => prev + deltaX);
    } else {
      const clientX = e.movementX;
      setPosition((prev) => prev + clientX);
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
    sliderRef.current.style.cursor = "grab";

    if (startX > position && activeSlide < slides?.length - 1) {
      openNextSlide();
    }

    if (startX < position && activeSlide > 0) {
      openPrevSlide();
    }
  };

  return (
    <div
      className={
        !isNaN(activeSlide) ? styles[isPreview ? previewSlide?.themeClass : slides[activeSlide].themeClass] : ""
      }
    >
      <BackgroundComponent deviceInfo={deviceInfo} />

      <div className={styles.headingWrapper}>
        <div className={styles.logo}>
          <IconLogo />
        </div>
        <Typography varient={"h3"} className={styles.title}>
          All I want this Valentine’s Day
        </Typography>
      </div>
      {isPreview && activeSlide === slides?.length - 1 ? (
        <div>
          <div className={styles.cardSubWrapper}>
            <div className={styles.card}>
              <div className={styles.heart}>{previewSlide?.card?.heart}</div>
              <Typography varient={"h4"} className={styles.cardTitle}>
                {formData?.title}
              </Typography>
              <Typography varient={"span"} className={styles.description}>
                {formData?.description}
              </Typography>
              <div className={`${styles.line} ${styles.topLine}`}></div>
              <div className={`${styles.line} ${styles.bottomLine}`}></div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={styles.cardMainWrapper}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          ref={sliderRef}
        >
          {!deviceInfo?.isMobile && (
            <>
              {activeSlide > 0 && (
                <IconLeft onClick={openPrevSlide} className={`${styles.arrows} ${styles.arrowPrev}`} />
              )}
              {activeSlide < slides?.length - 1 && (
                <IconRight onClick={openNextSlide} className={`${styles.arrows} ${styles.arrowNext}`} />
              )}
            </>
          )}
          <div className={styles.cardWrapper} style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
            {slides?.map((slide, index) => (
              <div key={`slide-${index}`} className={styles.cardSubWrapper}>
                <div className={`${styles.card} ${slide?.formUi ? styles.hasInput : ""}`}>
                  <div className={styles.heart}>{slide?.card?.heart}</div>
                  <Typography varient={"h4"} className={styles.cardTitle}>
                    {slide?.card?.title}
                  </Typography>
                  {slide?.formUi && activeSlide === 4 ? (
                    <div className={styles.inputWrapper}>
                      <div>
                        <input
                          onChange={(e) => {
                            setFormData((prev: any) => {
                              return { ...prev, title: e?.target?.value };
                            });
                          }}
                          value={formData?.title}
                          maxLength={32}
                          className={styles.input}
                          type="text"
                          placeholder="Title"
                        />
                        <Typography className={styles.limitText}>Max 32 characters</Typography>
                      </div>
                      <div>
                        <input
                          className={styles.input}
                          type="text"
                          placeholder="Description"
                          maxLength={70}
                          value={formData?.description}
                          onChange={(e) => {
                            setFormData((prev: any) => {
                              return { ...prev, description: e.target?.value };
                            });
                          }}
                        />
                        <Typography className={styles.limitText}>Max 70 characters</Typography>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Typography varient={"span"} className={styles.description}>
                        {slide?.card?.description}
                      </Typography>
                      <div className={`${styles.line} ${styles.topLine}`}></div>
                      <div className={`${styles.line} ${styles.bottomLine}`}></div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!isPreview && (
        <div className={styles.navigationBtnWrapper}>
          {slides?.map((_, i) => (
            <button
              key={`${i}-${i}`}
              className={`${activeSlide === i ? styles.active : ""} ${styles.navigationBtn}`}
              onClick={() => {
                setActiveSlide(i);
              }}
            ></button>
          ))}
        </div>
      )}
      {isPreview ? cardBtn.submitBtn : slides[activeSlide].formUi ? cardBtn.previewBtn : cardBtn.sendBtn}
      <ValentineShareModal
        {...{
          openPopup: isShareModalOpen,
          setOpenPopup: handleOpenShareModal,
          deviceInfo: deviceInfo,
        }}
      />
    </div>
  );
};

export default CardsComponent;
